import React, { useEffect, useRef } from 'react';
import { X, ExternalLink } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useAnalytics } from '../contexts/AnalyticsContext';
import { useAuth } from '../contexts/AuthContext';
import { shouldShowAds } from '../utils/adVisibility';

interface BannerAdProps {
  adUrl?: string;
  imageUrl?: string;
  title?: string;
  description?: string;
  ctaText?: string;
  position?: 'top' | 'bottom' | 'sidebar';
  onClose?: () => void;
  closable?: boolean;
  adId?: string; // Unique identifier for tracking
}

export default function BannerAd({
  adUrl = "https://www.motivamatch.com",
  imageUrl,
  title = "Boost Your Career",
  description = "Get personalized career insights",
  ctaText = "Learn More",
  position = 'top',
  onClose,
  closable = true,
  adId = `banner-${position}`
}: BannerAdProps) {
  const location = useLocation();
  const { user } = useAuth();
  const [isVisible, setIsVisible] = React.useState(true);
  const [isClosed, setIsClosed] = React.useState(false);
  const adRef = useRef<HTMLDivElement>(null);
  const { trackEvent, trackConversion } = useAnalytics();
  const hasTrackedImpression = React.useRef(false);

  // Check if ads should be shown
  if (!shouldShowAds(user, location.pathname)) {
    return null;
  }

  const handleClose = () => {
    setIsVisible(false);
    setIsClosed(true);
    if (onClose) {
      onClose();
    }
    // Store in localStorage to remember user's preference
    localStorage.setItem(`bannerAdClosed_${position}`, 'true');
  };

  // Check if user previously closed this ad
  React.useEffect(() => {
    const wasClosed = localStorage.getItem(`bannerAdClosed_${position}`);
    if (wasClosed === 'true') {
      setIsClosed(true);
      setIsVisible(false);
    }
  }, [position]);

  // Track ad impression when visible
  useEffect(() => {
    if (isClosed || !isVisible || hasTrackedImpression.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
            // Ad is at least 50% visible
            if (!hasTrackedImpression.current) {
              hasTrackedImpression.current = true;
              trackEvent('ad_impression', {
                ad_id: adId,
                ad_type: 'banner',
                ad_position: position,
                ad_url: adUrl,
                page: window.location.pathname,
              });
            }
          }
        });
      },
      { threshold: 0.5 }
    );

    if (adRef.current) {
      observer.observe(adRef.current);
    }

    return () => {
      if (adRef.current) {
        observer.unobserve(adRef.current);
      }
    };
  }, [isVisible, isClosed, adId, position, adUrl, trackEvent]);

  const handleClick = () => {
    // Track ad click conversion
    trackEvent('ad_click', {
      ad_id: adId,
      ad_type: 'banner',
      ad_position: position,
      ad_url: adUrl,
      cta_text: ctaText,
      page: window.location.pathname,
    });

    // Track as conversion
    trackConversion('ad_click', undefined, 'USD');

    window.open(adUrl, '_blank', 'noopener,noreferrer');
  };

  if (isClosed) {
    return null;
  }

  const positionClasses = {
    top: 'fixed top-0 left-0 right-0 z-50',
    bottom: 'fixed bottom-0 left-0 right-0 z-50',
    sidebar: 'sticky top-4'
  };

  const containerClasses = `
    ${positionClasses[position]}
    bg-gradient-to-r from-orange-500 to-purple-600
    text-white shadow-lg
    transition-all duration-300
    ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-full'}
  `;

  return (
    <div ref={adRef} className={containerClasses}>
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          {/* Ad Content */}
          <div 
            className="flex items-center gap-4 flex-1 cursor-pointer hover:opacity-90 transition-opacity"
            onClick={handleClick}
          >
            {imageUrl && (
              <img 
                src={imageUrl} 
                alt={title}
                className="w-16 h-16 object-cover rounded-lg hidden sm:block"
              />
            )}
            <div className="flex-1">
              <h3 className="font-bold text-sm sm:text-base mb-1">{title}</h3>
              <p className="text-xs sm:text-sm opacity-90 hidden md:block">{description}</p>
            </div>
            <button
              className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg font-semibold text-sm flex items-center gap-2 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                handleClick();
              }}
            >
              {ctaText}
              <ExternalLink className="w-4 h-4" />
            </button>
          </div>

          {/* Close Button */}
          {closable && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleClose();
              }}
              className="text-white/80 hover:text-white p-1 rounded-full hover:bg-white/20 transition-colors"
              aria-label="Close ad"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

